import { assertUnreachable, isNonEmpty, isNotDefined } from '@sgme/fp';

import { type OptionStrike, type SolvingStrike, type StrikeShortcut, strikeShortcuts } from '@/store/option/state.ts';

interface FormatOption extends Intl.NumberFormatOptions {
  locale?: string;
}

/**
 * Warning! possible loss of precision, only used for display purpose, not for calculation
 */
export function formatStrikeForDisplay(
  strike: OptionStrike | SolvingStrike | undefined,
  options?: FormatOption,
): string {
  if (isNotDefined(strike)) {
    return '';
  }

  switch (strike.type) {
    // TODO: solving
    case 'solving':
      return `solving to ${strike.way} ${new Intl.NumberFormat(options?.locale ?? 'en-EN', options).format(strike.solveValue)}`;
    case 'number':
      // FIXME: locale should be passed as argument
      return new Intl.NumberFormat(options?.locale ?? 'en-EN', options).format(strike.value);
    case 'shortcut':
      return strike.value;
    case 'delta':
      return `${strike.value}d`;
    default:
      assertUnreachable(strike, `Unknow strike type ${strike}`);
  }
}

export function formatStrikeWithPrecision(strike: OptionStrike | undefined): string {
  if (isNotDefined(strike)) {
    return '';
  }

  switch (strike.type) {
    case 'number':
      return strike.value.toString();
    case 'shortcut':
      return strike.value;
    case 'delta':
      return `${strike.value}d`;
    default:
      assertUnreachable(strike, `Unknow strike type ${strike}`);
  }
}

export function isStrikeShortcut(value: string): value is StrikeShortcut {
  return strikeShortcuts.includes(value.trim().toUpperCase() as unknown as any);
}

export function parseStrike(value: string): OptionStrike | undefined {
  if (isStrikeShortcut(value)) {
    return {
      type: 'shortcut',
      value: value.toUpperCase() as StrikeShortcut,
    };
  }

  const parsed = /^(?<amount>(-?([0-9]+(\.[0-9]*)?|\.[0-9]+))(?<delta>d?))$/i.exec(value.trim());
  if (isNotDefined(parsed)) {
    return undefined;
  }

  const numberValue = Number.parseFloat(parsed?.groups?.amount ?? '0');
  if (isNonEmpty(parsed?.groups?.delta)) {
    return {
      type: 'delta',
      value: numberValue,
    };
  }

  return {
    type: 'number',
    value: numberValue,
  };
}
